import FluxUtils from 'flux/utils';
import Immutable from 'immutable';

import SettingsActions from 'sharedreact/actions/SettingsActions';
import type { ActionType } from 'sharedreact/actions/generic/actions';
import AuthenticationConstants from 'sharedreact/constants/AuthenticationConstants';
import SettingsConstants from 'sharedreact/constants/SettingsConstants';
import Settings from 'sharedreact/data/Settings';
import type UnitSettings from 'sharedreact/data/UnitSettings';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';
import LanguageSupport from 'sharedreact/utils/LanguageSupport';

const Content: {
  settings: Settings,
  isDirty: boolean,
  userSettingsLoaded: boolean,
  isBusy: boolean,
  newLanguage: string | null,
  isVisible: boolean,
  shouldFetchUserSettings: boolean
} = {
  settings: new Settings(),
  isDirty: false,
  userSettingsLoaded: false,
  isBusy: false,
  newLanguage: null,
  isVisible: false,
  shouldFetchUserSettings: true
};

class State extends Immutable.Record(Content) {
}

class SettingsStore extends FluxUtils.ReduceStore<State, ActionType> {
  constructor () {
    super(AppDispatcher);
  }

  override getInitialState (): State {
    return new State();
  }

  getSettings (): Settings {
    return this.getState().settings;
  }

  getLanguage (): string {
    return this.getState().settings.language;
  }

  getUnits (): UnitSettings {
    return this.getState().settings.units;
  }

  getShouldTranslateText (): boolean {
    return this.getState().settings.shouldTranslateText;
  }

  getDisableShowDialogGoToJobFolder (): boolean {
    return this.getState().settings.warningsSettings.disableShowDialogGoToJobFolder;
  }

  areUserSettingsLoaded (): boolean {
    return this.getState().userSettingsLoaded;
  }

  isBusy (): boolean {
    return this.getState().isBusy;
  }

  override reduce (state: State, action: ActionType): State {
    switch (action.actionType) {
      case AuthenticationConstants.LOGIN_SUCCESS: {
        if (state.shouldFetchUserSettings) {
          SettingsActions.fetchFromServer();
        }
        return state;
      }
      case AuthenticationConstants.LOGOUT_SUCCESS: {
        // reset the settings: clear the settings, fetch language files for browser language and save language in settings
        SettingsActions.setDefaultSettings();
        return state;
      }
      case AuthenticationConstants.RELOGIN_FAIL: {
        // fetch language files for browser language and save language in settings
        SettingsActions.setDefaultSettings();
        return state;
      }

      case SettingsConstants.DONT_FETCH_USER_SETTINGS : {
        return state.set('shouldFetchUserSettings', false);
      }
      case SettingsConstants.SET_VALUE: {
        if (action.name === 'enableDevSettings') {
          if (!action.value) {
            LanguageSupport.setDoTranslate(true);
          }
          return state.set('settings', state.settings.setEnableDevSettings(action.value)).set('isDirty', true);
        }

        if (action.name === 'shouldTranslateText') {
          LanguageSupport.setDoTranslate(action.value);
        }

        if (action.name === 'language') {
          state = state.set('isBusy', false).set('newLanguage', null);
        }

        if (action.name === 'disableShowDialogGoToJobFolder') {
          return state
            .set('settings', state.settings
              .set('warningsSettings', state.settings.warningsSettings
                .set('disableShowDialogGoToJobFolder', action.value)
            )
          );
        }

        if (action.name === 'language' || (state.settings.get(action.name) !== action.value)) {
          return state.setIn(['settings', ...action.name.split('.')], action.value).set('isDirty', true);
        }

        return state;
      }
      case SettingsConstants.IS_SETTING_LANGUAGE: {
        return state.set('isBusy', true).set('newLanguage', action.value);
      }
      case SettingsConstants.GET_SUCCESS: {
        return state.set('settings', action.item).set('userSettingsLoaded', action.userSettingsLoaded);
      }
      case SettingsConstants.SAVE_SUCCESS: {
        return state.set('settings', action.item);
      }
      case SettingsConstants.TOGGLE: {
        const isVisible = !state.isVisible;

        if (!isVisible && state.isDirty) {
          SettingsActions.save(state.settings);
        }

        return state.set('isVisible', isVisible);
      }
      case SettingsConstants.HIDE: {
        if (state.isDirty) {
          SettingsActions.save(state.settings);
        }
        return state.set('isVisible', false).set('isDirty', false);
      }
    }

    return state;
  }
}

export default new SettingsStore();
